import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import './horizontal-product-thumbnail-style.scss';
import { IProps } from './types';
import useIsComDomain from '@src/hooks/useIsComDomain';

// Images & Icon
import MgsIcon from '@src/images/icons/mgs-logo.png';
import { MgsLogoIcon } from '@components/ProductPage/ProductDetailSummary';

// Components
import Typography from '@src/componentsNew/Typography/Typography';
import WishListButton from '@src/componentsNew/Buttons/WishListButton/WishListButton';
import StarRatingIcon from '@src/componentsNew/Icons/StarRatingIcon';

import { gaV4SearchProductDetail, getDBIdFromGraphqlId, getHighestDiscountVoucher } from "@utils";
import { isNew, getPriceInfoFromProduct, formatPrice } from '@src/utils';
import { shouldShowElementAccordingToDomain } from '@src/utils/domainCheck';
import { HIDE_VOUCHER_BRAND_IDS } from '@src/utils/constants';

const HorizontalProductThumbnail = ({ product }: IProps) => {
  const history = useHistory();
  const isComDomain = useIsComDomain();

  const { originalPrice, finalPrice, isOnSale, discountPercent, isOutOfStock } = getPriceInfoFromProduct(product);

  const [numOfVariants] = useState(product?.variants?.length);
  const [showVariants] = useState(product?.productType?.hasVariants && 1 < numOfVariants);

  const isIncludeSearchUrl = (product) => {
    const handleOnDetailSearchEvent = async (productId, productName, brandId, brandName, keyword, searchText) => {
      await gaV4SearchProductDetail(productId, productName, brandId, brandName, keyword, searchText);
    };

    const currentLocation = history.location;

    if (currentLocation.search.includes('?refer=search')) {
      const urlParams = new URLSearchParams(currentLocation.search);
      const keyword = urlParams.get('word');
      const searchText = urlParams.get('text');

      handleOnDetailSearchEvent(
        getDBIdFromGraphqlId(product.id, 'Product'),
        product.name,
        getDBIdFromGraphqlId(product.brandLink.id, 'Brand'),
        product.brand,
        keyword ? keyword : '',
        searchText ? searchText : '',
      );
    } else if (currentLocation.pathname.includes('/search-result')) {
      const urlParams = new URLSearchParams(currentLocation.search);
      const keyword = urlParams.get('q');
      const searchText = urlParams.get('q');

      handleOnDetailSearchEvent(
        getDBIdFromGraphqlId(product.id, 'Product'),
        product.name,
        getDBIdFromGraphqlId(product.brandLink.id, 'Brand'),
        product.brand,
        keyword ? keyword : '',
        searchText ? searchText : '',
      );
    }
  };

  const handleClickThumbnail = (product) => {
    isIncludeSearchUrl(product);
    history.push(product.urlForGraphql);
  };

  return product ? (
    <div className="horizontal-product-thumbnail-wrapper" onClick={() => handleClickThumbnail(product)}>
      {/* 상품 이미지 영역 */}
      <div className="product-image-wrapper">
        <LazyLoadImage className="product-image" src={product.thumbnail.url} alt={product.name} effect="blur" />

        {/* Domain Revert Comment */}
        {/* {
            shouldShowElementAccordingToDomain(isComDomain, product.productType.isMgs) ?
              <span className="wishlist-btn-wrapper">
                <WishListButton
                  productPrice={product?.availability?.priceRange?.start?.gross?.amount ?? 0}
                  previousPage="product_thumbnail"
                  previousPath={history.location.pathname}
                  productName={product.name}
                  productId={product.id}
                  isSelected={product.isOnWishlist}
                />
              </span> : null
          } */}
        <span className="wishlist-btn-wrapper">
          <WishListButton
            productPrice={product?.availability?.priceRange?.start?.gross?.amount ?? 0}
            previousPage="product_thumbnail"
            previousPath={history.location.pathname}
            productName={product.name}
            productId={product.id}
            isSelected={product.isOnWishlist}
          />
        </span>
      </div>

      {/* 상품 정보 영역 */}
      <div className="product-description-wrapper">
        <Typography cName="brand-name" variant="subtitle2" component="span" style={{ color: '#090A0FDE' }}>
          {product.brand}
        </Typography>

        <div className="product-name">
          {isNew(product?.publicationDate) ? (
            <Typography variant="subtitle2" component="span" style={{ color: '#FF3399' }}>
              {`New `}
            </Typography>
          ) : null}
          <Typography variant="body3" component="span" style={{ color: '#666666' }}>
            {product.name}
          </Typography>
        </div>

        {showVariants || product.productType.isMgs ? (
          <div className="variants-mgs-wrapper">
            {showVariants && (
              <Typography variant="caption" component="span" style={{ color: '#11141D99' }}>
                {`${numOfVariants} Variants`}
              </Typography>
            )}
            {product.productType.isMgs && <MgsLogoIcon src={MgsIcon} style={{ width: '4rem' }} />}
          </div>
        ) : null}

        <div className="price-wrapper">
          <Typography variant="price" component="span">
            {formatPrice(finalPrice)}
          </Typography>
          {isOnSale && (
            <>
              <Typography variant="price" component="span" style={{ color: '#FF3399' }}>
                {discountPercent}%
              </Typography>
              <Typography variant="strikethrough2" component="span" style={{ color: '#11141D61' }}>
                {formatPrice(originalPrice)}
              </Typography>
            </>
          )}
        </div>

        {isOutOfStock && (
          <Typography cName="out-of-stock-badge" variant="body2" component="span" style={{ color: '#FFFFFF' }}>
            Out of stock
          </Typography>
        )}

        {!isOutOfStock && (!product.productType.isCodRestricted || product.hasVoucher || product.hasFreebieEvent) ? (
          <div className="product-type-wrapper">
            {!product.productType.isCodRestricted ? <span className="product-type-item">COD</span> : null}
            {product.hasVoucher &&
            !HIDE_VOUCHER_BRAND_IDS.includes(getDBIdFromGraphqlId(product?.brandLink?.id, 'Brand').toString()) ? (
              <span className="product-type-item">Get for {getHighestDiscountVoucher(product)}</span>
            ) : null}
            {product.hasFreebieEvent ? <span className="product-type-item">Freebie</span> : null}
          </div>
        ) : null}

        {product?.productReviewsAggregation?.rating ? (
          <div className="rating-area">
            <StarRatingIcon />
            <Typography variant="caption" component="span" style={{ color: '#212121' }}>
              {product?.productReviewsAggregation?.rating.toFixed(1)}
            </Typography>
            {product.productReviewsAggregation?.reviewCount ? (
              <Typography variant="caption" component="span" style={{ color: '#666666' }}>
                {`(${product.productReviewsAggregation?.reviewCount})`}
              </Typography>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  ) : null;
};

export default HorizontalProductThumbnail;
